import { useEffect, useState } from "react";
import { SUPPORTED_LANGUAGES } from "./assets/localization";
import HostLanguage from "./utils/HostLanguage";
import { Chat } from "./widgets/Chat/Chat";

function App({
  assistant,
  isEditor = false,
  editorStyle,
  language,
  mode = "0",
}: {
  assistant: { id?: string; name?: string };
  isEditor: any;
  editorStyle: any;
  language: any;
  mode?: string;
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // The host site may change language after the mount (PlutoniosChat.setLanguage,
  // or `<html lang>` when the embed follows it); a language it named before we
  // mounted wins over the one init() read. The backoffice preview keeps its own.
  const [hostLanguage, setHostLanguage] = useState(
    () => (isEditor ? undefined : HostLanguage.get()) ?? language
  );

  useEffect(() => {
    if (isEditor) return;
    // A change between the first render and this subscription is not lost.
    const latest = HostLanguage.get();
    if (latest) setHostLanguage(latest);
    return HostLanguage.subscribe(setHostLanguage);
  }, [isEditor]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // `language` arrives as the host page's raw tag (document.documentElement.lang
  // || navigator.language), so it can be anything: "de", "de-DE", "pt-BR".
  // Reduce it to a locale we ship, defaulting to English — which is also what the
  // backend serves when the hotel has not configured the guest's language.
  //
  // Undefined when nobody named one at all. Only the dedicated page can be in
  // that position (the embed loader always sends the host page's), and there the
  // assistant's configured standalone language answers instead: Chat asks the
  // backend without a locale and renders the one it serves.
  const getRequestedLanguage = () => {
    const raw = String(hostLanguage ?? "").trim();

    if (!raw) return undefined;

    const region = raw.toLowerCase().split(/[-_]/)[0];

    return SUPPORTED_LANGUAGES.includes(region) ? region : "en";
  };

  useEffect(() => {
    // Load external stylesheets and scripts
    const loadExternalResources = () => {
      const fontAwesomeCdn = document.createElement("link");
      fontAwesomeCdn.rel = "stylesheet";
      fontAwesomeCdn.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.6.0/css/all.min.css";
      fontAwesomeCdn.integrity =
        "sha512-Kc323vGBEqzTmouAECnVceyQqyqdsSiqLQISBL29aUW4U/M7pSPA/gEUZQqv1cwx4OnYxTxve5UMg5GT6L4JJg==";
      fontAwesomeCdn.crossOrigin = "anonymous";
      fontAwesomeCdn.referrerPolicy = "no-referrer";
      document.head.appendChild(fontAwesomeCdn);

      const linkifyJs = document.createElement("script");
      linkifyJs.src =
        "https://cdn.jsdelivr.net/npm/linkifyjs@4.1.3/dist/linkify.min.js";
      document.head.appendChild(linkifyJs);
    };

    loadExternalResources();
  }, []);

  return (
    <div className="plutonios-chat-widget-root">
      <Chat
        mode={mode}
        assistant={assistant}
        isEditor={isEditor}
        editorStyle={editorStyle}
        isMobile={isMobile}
        language={getRequestedLanguage()}
      />
    </div>
  );
}

export default App;
